import React, { useEffect, useState } from 'react'
import { Drawer, Tabs, Button, Select, Space, Card, Tag, Transfer, message, Spin, Checkbox, Tooltip, Alert } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useConfig } from '../../contexts/ConfigContext'
import { useCompany } from '../../contexts/CompanyContext'
import { useUser } from '../../contexts/UserContext'
import { useSecureRequest } from '../../hooks/useSecureRequest'

interface UserDetailDrawerProps {
  userId: number | null
  open: boolean
  onClose: () => void
}

function UserDetailDrawer({ userId, open, onClose }: Readonly<UserDetailDrawerProps>) {
  const { config } = useConfig()
  const { companies } = useCompany()
  const { user } = useUser()
  const { securePost } = useSecureRequest()

  const isSelf = user?.id === userId && user?.adminScope !== 'system'

  const [loading, setLoading] = useState(false)
  const [userDetails, setUserDetails] = useState<any>(null)
  
  // Tab Empresas & Módulos
  const [userCompanies, setUserCompanies] = useState<any[]>([])
  const [allCompanies, setAllCompanies] = useState<any[]>([])
  const [selectedCompanyToAdd, setSelectedCompanyToAdd] = useState<number | null>(null)

  // Tab Roles
  const [companyRoles, setCompanyRoles] = useState<any[]>([])
  const [selectedRoles, setSelectedRoles] = useState<number[]>([])
  const [rolesSaving, setRolesSaving] = useState(false)

  const fetchData = async () => {
    if (!userId || !config.key) return
    setLoading(true)
    try {
      // 1. Obtener detalles básicos del usuario
      const userRes = await securePost('/user-management/get-user-full-details', { 
        session: config.session, 
        idUser: userId 
      })
      setUserDetails(userRes)
      
      // Mapear los roles actuales del usuario en la empresa activa
      if (userRes.roles) {
        const currentCompanyId = companies.current?.id
        const userRolesInCurrentCompany = userRes.roles
          .filter((r: any) => {
            const compId = r.assigned_company_id ?? r.company_id;
            return compId === currentCompanyId;
          })
          .map((r: any) => r.id)
        setSelectedRoles(userRolesInCurrentCompany)
      }

      // 2. Obtener empresas y módulos del usuario
      const compsRes = await securePost('/user-management/get-user-companies-with-modules', { 
        session: config.session, 
        user_id: userId 
      })
      setUserCompanies(compsRes.companies || [])

      // 3. Obtener todas las empresas (para el selector de agregar)
      const allCompsRes = await securePost('/company/list', { session: config.session })
      setAllCompanies(allCompsRes.companyList || [])

      // 4. Obtener roles disponibles para la empresa activa
      if (companies.current?.id) {
        const rolesRes = await securePost('/role/get-company-roles', { 
          session: config.session, 
          company_id: companies.current?.id 
        })
        setCompanyRoles(rolesRes.roles || [])
      }

    } catch (err) {
      console.error(err)
      message.error('Error al cargar detalles del usuario')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && userId) {
      fetchData()
    }
  }, [open, userId, companies.current?.id])

  const handleAddCompany = async () => {
    if (!selectedCompanyToAdd || !userId) return
    try {
      await securePost('/user-management/assign-company', {
        session: config.session,
        user_id: userId,
        company_id: selectedCompanyToAdd
      })
      message.success('Empresa asignada exitosamente')
      setSelectedCompanyToAdd(null)
      fetchData()
    } catch (err) {
      console.error(err)
      message.error('Error al asignar empresa')
    }
  }

  const handleRemoveCompany = async (companyId: number) => {
    if (!userId) return
    try {
      await securePost('/user-management/remove-company', {
        session: config.session,
        user_id: userId,
        company_id: companyId
      })
      message.success('Empresa desvinculada exitosamente')
      fetchData()
    } catch (err) {
      console.error(err)
      message.error('Error al desvincular empresa')
    }
  }

  const handleModulesChange = async (companyId: number, nextTargetKeys: React.Key[]) => {
    if (!userId) return
    try {
      const moduleIds = nextTargetKeys.map(k => Number.parseInt(k.toString()))
      await securePost('/user-management/assign-modules', {
        session: config.session,
        user_id: userId,
        company_id: companyId,
        modules: moduleIds
      })
      message.success('Módulos actualizados exitosamente')
      fetchData()
    } catch (err) {
      console.error(err)
      message.error('Error al actualizar módulos')
    }
  }

  const handleRolesSave = async () => {
    if (!userId || !companies.current?.id) return
    setRolesSaving(true)
    try {
      await securePost('/user-management/assign-roles', {
        session: config.session,
        user_id: userId,
        company_id: companies.current.id,
        roles: selectedRoles
      })
      message.success('Roles actualizados exitosamente')
      fetchData()
    } catch (err) {
      console.error(err)
      message.error('Error al actualizar roles')
    } finally {
      setRolesSaving(false)
    }
  }

  const unassignedCompanies = allCompanies.filter(
    (c) => !userCompanies.some((uc) => uc.id === c.id)
  )

  const tabItems = [
    {
      key: '1',
      label: 'Empresas & Módulos',
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h4>Vincular Nueva Empresa</h4>
            <Space>
              <Select
                style={{ width: 300 }}
                placeholder="Seleccionar empresa"
                value={selectedCompanyToAdd}
                onChange={setSelectedCompanyToAdd}
                options={unassignedCompanies.map((c) => ({ value: c.id, label: c.name }))}
              />
              <Button type="primary" onClick={handleAddCompany} disabled={!selectedCompanyToAdd}>
                Vincular
              </Button>
            </Space>
          </div>

          <Space direction="vertical" style={{ width: '100%' }}>
            {userCompanies.map((uc) => {
              const dataSource = uc.availableModules.map((m: any) => ({
                key: m.id.toString(),
                title: m.name
              }))
              const targetKeys = uc.assignedModules.map((m: any) => m.id.toString())

              return (
                <Card 
                  key={uc.id} 
                  title={`🏢 ${uc.name}`}
                  extra={<Button danger size="small" onClick={() => handleRemoveCompany(uc.id)}>Desvincular</Button>}
                >
                  <p>Módulos habilitados para el usuario en esta empresa:</p>
                  <Transfer
                    dataSource={dataSource}
                    titles={['Disponibles', 'Asignados']}
                    targetKeys={targetKeys}
                    onChange={(nextTargetKeys) => handleModulesChange(uc.id, nextTargetKeys)}
                    render={item => item.title as string}
                    listStyle={{ width: 300, height: 250 }}
                  />
                </Card>
              )
            })}
            {userCompanies.length === 0 && <p>El usuario no tiene empresas vinculadas.</p>}
          </Space>
        </div>
      )
    },
    {
      key: '2',
      label: 'Roles',
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <strong>Empresa activa: </strong>
            <Tag color="blue">{companies.current?.name}</Tag>
            <p style={{ marginTop: 8, color: 'gray' }}>
              Los roles se asignan por empresa. Asegúrese de tener la empresa correcta seleccionada en el menú superior.
            </p>
          </div>

          {isSelf && (
            <Alert
              message="Restricción de seguridad"
              description="Por motivos de seguridad, no puedes modificar tus propios roles. Solicita este cambio a otro administrador."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Space direction="vertical" style={{ width: '100%', gap: 10 }}>
            {companyRoles
              .filter((role) => role.name !== 'SUPER_ADMIN' || user?.adminScope === 'system')
              .map((role) => (
              <div
                key={role.id}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #f0f0f0',
                  background: '#fafafa'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Checkbox
                    disabled={isSelf}
                    checked={selectedRoles.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRoles([...selectedRoles, role.id])
                      } else {
                        setSelectedRoles(selectedRoles.filter(id => id !== role.id))
                      }
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{role.name}</span>
                    <Tag color={role.scope === 'system' ? 'purple' : 'blue'} style={{ marginLeft: 8 }}>
                      {role.scope === 'system' ? 'SISTEMA' : 'EMPRESA'}
                    </Tag>
                  </Checkbox>
                  <Tooltip title={role.description || 'Sin descripción'} placement="topRight">
                    <InfoCircleOutlined style={{ color: role.description ? '#1890ff' : '#bfbfbf', cursor: 'pointer', fontSize: 16 }} />
                  </Tooltip>
                </div>
                {role.description && (
                  <div style={{ marginTop: 4, marginLeft: 24, fontSize: 12, color: '#595959' }}>
                    {role.description}
                  </div>
                )}
              </div>
            ))}
            {companyRoles.filter((role) => role.name !== 'SUPER_ADMIN' || user?.adminScope === 'system').length === 0 && (
              <p style={{ color: '#8c8c8c' }}>No hay roles disponibles para esta empresa.</p>
            )}
          </Space>
          <div style={{ marginTop: 24 }}>
            <Button type="primary" onClick={handleRolesSave} loading={rolesSaving} disabled={isSelf}>
              Guardar Roles
            </Button>
          </div>
        </div>
      )
    }
  ]

  return (
    <Drawer
      title="Detalle del Usuario"
      placement="right"
      width={720}
      onClose={onClose}
      open={open}
    >
      <Spin spinning={loading}>
        {userDetails && (
          <div style={{ marginBottom: 24 }}>
            <h3>{userDetails.name}</h3>
            <p><strong>Email:</strong> {userDetails.email}</p>
            <p><strong>Documento:</strong> {userDetails.document_type} {userDetails.document_number}</p>
            <Tag color={userDetails.is_active ? 'green' : 'red'}>
              {userDetails.is_active ? 'Activo' : 'Inactivo'}
            </Tag>
          </div>
        )}
        <Tabs defaultActiveKey="1" items={tabItems} />
      </Spin>
    </Drawer>
  )
}

export default UserDetailDrawer
